import React, { useEffect, useState } from "react";

import { animated, useSpring } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import styled, { css } from "styled-components";

export interface ScrollBarProps {
  referenceContainer: React.RefObject<HTMLDivElement>;
  axis?: "x" | "y";
}
interface Props extends ScrollBarProps {
  slider: {
    width?: number;
    height?: number;
  };
  refHeight?: number;
  refWidth?: number;
}

const Outer = styled.div<Props>`
  position: absolute;
  --thickness: 7px;
  &:hover {
    --thickness: 12px;
  }
  z-index: 99999;
  padding: 1px;
  transition: 200ms;

  ${({ axis, refWidth, refHeight }) => {
    if (!axis || axis === "x") {
      return css`
        transition-property: height;
        bottom: 0;
        width: ${refWidth}px;
        height: calc(var(--thickness) + 2px);
      `;
    }
    return css`
      right: 0;
      height: ${refHeight}px;
      transition-property: width;
      width: calc(var(--thickness) + 2px);
    `;
  }};
`;
const Inner = styled(animated.div)<Props>`
  position: absolute;
  touch-action: none;
  /* background-color: ${({ theme }) => theme.colors.palette["text/800"]}; */
  border-radius: 10px;
  transition: 200ms;
  ${({ axis, slider }) => {
    if (!axis || axis === "x") {
      return css`
        transition-property: height;
        height: var(--thickness);
        width: ${slider?.width}px;
      `;
    }
    return css`
      transition-property: width;
      width: var(--thickness);
      height: ${slider?.height}px;
    `;
  }};
`;

const ScrollBar = ({ referenceContainer, axis }: ScrollBarProps) => {
  const [{ x, y }, api] = useSpring(() => ({ x: 0, y: 0 }));
  const outer = React.useRef<HTMLDivElement>(null);
  const inner = React.useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  // the maximum value the slider can move to (container width - slider width)
  const [maxX, setMaxX] = useState(0);
  const [maxY, setMaxY] = useState(0);
  // the maximum distance the reference container can be scrolled by
  const [maxSlideX, setMaxSlideX] = useState(0);
  const [maxSlideY, setMaxSlideY] = useState(0);
  // the current scroll position in slider px
  const [posX, setPosX] = useState(0);
  const [posY, setPosY] = useState(0);
  // the width of the slider
  const [sliderWidth, setSliderWidth] = useState(500);
  const [sliderHeight, setSliderHeight] = useState(500);
  // if the user is currently dragging the slider
  const [isScrolling, setIsScrolling] = useState(false);

  const [refWidth, setRefWidth] = useState(0);
  const [refHeight, setRefHeight] = useState(0);

  useEffect(() => {
    if (outer) {
      setMaxX((outer.current?.clientWidth || 0) - sliderWidth - 2);
      setMaxY((outer.current?.clientHeight || 0) - sliderHeight - 2);
    }
  }, [outer, sliderWidth, sliderHeight]);

  const updateSlider = () => {
    if (!referenceContainer?.current) return;

    const {
      clientWidth: cw,
      clientHeight,
      scrollWidth: sw,
      scrollHeight,
    } = referenceContainer.current;
    const paddingRight = Number.parseFloat(
      getComputedStyle(referenceContainer.current).paddingRight,
    );
    const clientWidth = cw + 1;
    const scrollWidth = sw;
    const innerWidth = clientWidth - paddingRight;

    setShow(
      ((!axis || axis === "x") && clientWidth < scrollWidth) ||
        (axis === "y" && clientHeight < scrollHeight),
    );

    if (!axis || axis === "x") {
      referenceContainer.current.style.overflowX = "auto";
    }
    if (axis === "y") {
      referenceContainer.current.style.overflowY = "auto";
    }

    referenceContainer.current.classList.add("hide-scrollbar");

    setMaxSlideX(scrollWidth - clientWidth);
    setMaxSlideY(scrollHeight - clientHeight);

    setSliderWidth(Math.max(50, clientWidth / (scrollWidth / innerWidth)));

    setSliderHeight(Math.max(50, clientHeight / (scrollHeight / clientHeight)));

    setRefWidth(innerWidth);
    setRefHeight(clientHeight);
  };

  useEffect(() => {
    const observer = new ResizeObserver(updateSlider);
    if (referenceContainer.current) {
      for (let i = 0; i < referenceContainer.current.children.length; i++) {
        observer.observe(referenceContainer.current.children[i]);
      }
    }
    return () => {
      observer.disconnect();
    };
  }, []);
  useEffect(() => {
    window.addEventListener("resize", updateSlider);
    return () => {
      window.removeEventListener("resize", updateSlider);
    };
  }, []);

  useEffect(() => {
    const reference = referenceContainer?.current;
    const handleScroll = () => {
      if (isScrolling) return;
      const x =
        !axis || axis === "x"
          ? (maxX / maxSlideX) * (reference?.scrollLeft || 0)
          : 0;
      const y =
        axis === "y" ? (maxY / maxSlideY) * (reference?.scrollTop || 0) : 0;
      api.start({
        x,
        y,
        immediate: true,
        // config: { tension: 50, friction: 50 },
      });
      setPosX(x);
      setPosY(y);
    };
    reference?.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      reference?.removeEventListener("scroll", handleScroll);
    };
  }, [referenceContainer, api, maxX, maxSlideX, maxY, maxSlideY, isScrolling]);

  // Set the drag hook and define component movement based on gesture data
  const bind = useDrag(({ down, movement: [mx, my], canceled, last }) => {
    const x =
      !axis || axis === "x" ? Math.max(0, Math.min(maxX, mx + posX)) : 0;
    const y = axis === "y" ? Math.max(0, Math.min(maxY, my + posY)) : 0;

    if (!last) {
      api.start({ x: down ? x : 0, y: down ? y : 0, immediate: down });
      if (!axis || axis === "x")
        referenceContainer?.current?.scrollTo({
          left: x * (maxSlideX / maxX),
        });
      if (axis === "y")
        referenceContainer?.current?.scrollTo({
          top: y * (maxSlideY / maxY),
        });
    } else {
      setIsScrolling(false);
      setPosX(x);
      setPosY(y);
      return;
    }
    setIsScrolling(true);
    if (canceled) {
      api.start({ x: maxX, y: maxY, immediate: true });
    }
  });
  return (
    <Outer
      ref={outer}
      axis={axis}
      refWidth={refWidth}
      refHeight={refHeight}
      className="scroll-bar"
    >
      <Inner
        {...bind()}
        ref={inner}
        slider={{ width: sliderWidth, height: sliderHeight }}
        style={{ x, y, opacity: show ? 1 : 0 }}
        axis={axis}
        className="bg-text-subdued opacity-30"
      />
    </Outer>
  );
};

export default ScrollBar;
